import { z } from "zod"

import type { BlockedSender } from "@productlane/zero"
import { normalizeEmail } from "@productlane/api/src/lib/contact"

// Local version of EmailContactSchema to avoid importing from @productlane/api
export const EmailContactSchema = z.object({
	name: z.string(),
	email: z.string(),
	mailboxHash: z.string().optional().default(""),
})

export type EmailContact = z.infer<typeof EmailContactSchema>

function deriveNameFromEmail(email: string): string {
	return (
		email
			.split("@")[0]
			?.replace(/[._+-]+/g, " ")
			.trim() || email
	)
}

// Returns a human name, deriving one from the email when name is missing or is itself an email.
export function getContactDisplayName(contact: {
	name?: string | null
	email?: string | null
}): string | undefined {
	const name = contact.name?.trim()
	if (name && !name.includes("@")) return name
	if (contact.email) return deriveNameFromEmail(contact.email)
	return name || undefined
}

/**
 * Checks if an email address is blocked (either directly or via domain blocking)
 * @param email - The email address to check
 * @param blockedEmails - Array of blocked email senders
 * @returns true if the email is blocked, false otherwise
 */
export function isEmailBlocked(
	email: string | null | undefined,
	blockedEmails: BlockedSender[] | undefined,
): boolean {
	if (!email || !blockedEmails) return false

	const normalizedEmail = normalizeEmail(email)
	const normalizedDomain = normalizedEmail.split("@")[1] ?? null

	return blockedEmails.some((blocked) => {
		const normalizedBlockedValue = normalizeEmail(blocked.value)
		if (blocked.type === "EMAIL") {
			return normalizedBlockedValue === normalizedEmail
		}
		if (blocked.type === "DOMAIN" && normalizedDomain) {
			return normalizedBlockedValue === normalizedDomain
		}
		return false
	})
}

/**
 * Finds the blocked sender entry for an email address (either direct email or domain block)
 * @param email - The email address to check
 * @param blockedEmails - Array of blocked email senders
 * @returns The blocked sender ID if found, null otherwise
 */
export function getBlockedSenderId(
	email: string | null | undefined,
	blockedEmails: BlockedSender[] | undefined,
): string | null {
	if (!email || !blockedEmails) return null

	const normalizedEmail = normalizeEmail(email)
	const normalizedDomain = normalizedEmail.split("@")[1] ?? null

	const blocked = blockedEmails.find((blocked) => {
		const normalizedBlockedValue = normalizeEmail(blocked.value)
		if (blocked.type === "EMAIL") {
			return normalizedBlockedValue === normalizedEmail
		}
		if (blocked.type === "DOMAIN" && normalizedDomain) {
			return normalizedBlockedValue === normalizedDomain
		}
		return false
	})
	return blocked?.id ?? null
}
